import {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    AuthUser,
    fetchAuthSession,
    getCurrentUser,
    JWT
} from 'aws-amplify/auth';
import type {AuthStatus} from '@flexinet/auth-components';
import {AuthContext} from '../context/AuthContext';

interface IAuthProvider {
    children?: ReactNode
}

export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {

    const [loading, setLoading] = useState(true)
    const [user, setUser] = useState<AuthUser | undefined>();
    // FNP-1733: what getCurrentUser() answered, once it has answered.
    const [userLookup, setUserLookup] = useState<'authenticated' | 'anonymous' | undefined>();
    const [idToken, setIdToken] = useState<JWT | undefined>();
    const [accessToken, setAccessToken] = useState<JWT | undefined>();
    const [cognitoGroups, setCognitoGroups] = useState<string[] | undefined>();
    const [role, setRole] = useState<string | undefined>();

    const fetchCognitoUser = useCallback(async () => {

        try {
            const cognitoUser = await getCurrentUser()
            setUser(cognitoUser)
            setUserLookup('authenticated')
        } catch (error) {
            // The normal signed-out visit lands here: getCurrentUser()
            // throws when nobody is signed in.
            console.trace(
                'AUTH CONTEXT. FETCH USER ERROR',
                error
            );
            setUserLookup('anonymous')
        }

    }, []);

    const fetchSession = async () => {

        try {
            const {
                accessToken,
                idToken
            } = (await fetchAuthSession()).tokens ?? {};

            setIdToken(idToken);
            setAccessToken(accessToken);

            if (typeof idToken !== 'undefined'
                && idToken.payload['cognito:groups'] !== undefined
                && Array.isArray(idToken.payload['cognito:groups'])
                && idToken.payload['cognito:groups'].length > 0) {

                setCognitoGroups(idToken.payload['cognito:groups'] as string[]);

            }

            // if (accessToken && idToken) {
            //     const cognitoAuthTokens = await refreshAuthTokens();
            //     console.debug('cognitoAuthTokens', cognitoAuthTokens)
            // }

        } catch (error) {
            console.trace(
                'AUTH CONTEXT. FETCH SESSION ERROR',
                error
            );
        } finally {
            setLoading(false);
        }

    };

    useEffect(() => {
        fetchCognitoUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        fetchSession();
    }, []);


    useEffect(() => {

        setRole('User')

        if (typeof idToken !== 'undefined'
            && idToken.payload['cognito:groups'] !== undefined
            && Array.isArray(idToken.payload['cognito:groups'])
            && idToken.payload['cognito:groups'].length > 0) {

            const managedUserItem = idToken.payload['cognito:groups'].find((item: unknown) => item === 'managed_user')
            if (managedUserItem) {
                setRole('ManagedUser')
            }

            const orgAdminItem = idToken.payload['cognito:groups'].find((item: unknown) => item === 'org_admin')
            if (orgAdminItem) {
                setRole('OrgAdmin')
            }

            const adminItem = idToken.payload['cognito:groups'].find((item: unknown) => item === 'admin')
            if (adminItem) {
                setRole('Admin')
            }

        }

    }, [idToken])


    // Resolving until both the user and the session lookups have settled.
    const status: AuthStatus = loading || typeof userLookup === 'undefined' ? 'resolving' : userLookup;

    return (
        <AuthContext.Provider value={{
            loading,
            user,
            status,
            idToken,
            accessToken,
            cognitoGroups,
            role,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
